import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState
} from "react"
import { useLocation } from "react-router-dom"
import { APP_CONSTANTS } from "../../config/constants"

interface HelpContextType {
    showHelpButton: boolean
    showHelpPopup: boolean
    setShowHelpButton: (show: boolean) => void
    setShowHelpPopup: (show: boolean) => void
    handleShowHelpPopup: () => void
    handleCloseHelpPopup: () => void
    handleWhatsAppRedirect: () => void
    resetHelpTimer: () => void
    showHelpAfterErrors: (errorCount: number) => void
}

const HelpContext = createContext<HelpContextType | undefined>(undefined)

export const useHelp = () => {
    const context = useContext(HelpContext)
    if (!context) {
        throw new Error("useHelp deve ser usado dentro de um HelpProvider")
    }
    return context
}

interface HelpProviderProps {
    children: ReactNode
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
    const [showHelpButton, setShowHelpButton] = useState(false)
    const [showHelpPopup, setShowHelpPopup] = useState(false)
    const [helpTimer, setHelpTimer] = useState<NodeJS.Timeout | null>(null)
    const location = useLocation()

    const isTrackingPage = location.pathname.includes("/rastreamento")

    const handleShowHelpPopup = () => {
        setShowHelpPopup(true)
    }

    const handleCloseHelpPopup = () => {
        setShowHelpPopup(false)
    }

    const handleWhatsAppRedirect = () => {
        // Número do WhatsApp da LogPlace
        const whatsappNumber = APP_CONSTANTS.WHATSAPP_NUMBER

        const message = isTrackingPage
            ? "Olá! 👋\n\n" +
              "Estou no site da LogPlace tentando rastrear uma encomenda, mas não estou conseguindo encontrar as informações.\n\n" +
              "Vocês poderiam me ajudar?\n\n" +
              "Obrigado! 😊"
            : "Olá! 👋\n\n" +
              "Estou no site da LogPlace e gostaria de algumas informações.\n\n" +
              "Vocês poderiam me ajudar?\n\n" +
              "Obrigado! 😊"

        const encodedMessage = encodeURIComponent(message)
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`
        window.open(whatsappUrl, "_blank")
    }

    const resetHelpTimer = () => {
        // Limpa o timer atual
        if (helpTimer) {
            clearTimeout(helpTimer)
        }

        // Se não estiver na página de tracking e o botão não estiver visível, inicia o timer de 30 segundos
        if (!isTrackingPage && !showHelpButton) {
            const timer = setTimeout(() => {
                setShowHelpButton(true)
            }, 10000) // 30 segundos

            setHelpTimer(timer)
        }
    }

    const showHelpAfterErrors = (errorCount: number) => {
        // Se chegou a 1 erro consecutivo, mostra o botão flutuante
        if (errorCount >= 1) {
            setShowHelpButton(true)
        }
    }

    // Efeito para gerenciar o timer baseado na rota
    useEffect(() => {
        // Fecha apenas o popup quando muda de página
        setShowHelpPopup(false)

        // Limpa o timer anterior
        if (helpTimer) {
            clearTimeout(helpTimer)
        }

        // Se não estiver na página de tracking e o botão não estiver visível, inicia o timer
        if (!isTrackingPage && !showHelpButton) {
            const timer = setTimeout(() => {
                setShowHelpButton(true)
            }, 10000) // 30 segundos

            setHelpTimer(timer)
        }

        // Cleanup function
        return () => {
            if (helpTimer) {
                clearTimeout(helpTimer)
            }
        }
    }, [location.pathname, isTrackingPage, showHelpButton])

    const value: HelpContextType = {
        showHelpButton,
        showHelpPopup,
        setShowHelpButton,
        setShowHelpPopup,
        handleShowHelpPopup,
        handleCloseHelpPopup,
        handleWhatsAppRedirect,
        resetHelpTimer,
        showHelpAfterErrors
    }

    return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>
}
